<template>
  <AcademyLayout @enroll="enrollOpen = true">
    <div class="ac-wrap sch-page">
      <div
        v-if="trainingSchedules.length > 1"
        class="sch-switch"
        role="tablist"
        aria-label="选择培训安排"
      >
        <button
          v-for="(item, idx) in trainingSchedules"
          :key="item.id"
          type="button"
          role="tab"
          class="sch-switch__btn"
          :class="{ 'is-active': activeId === item.id }"
          :aria-selected="activeId === item.id"
          @click="activeId = item.id"
        >
          <span class="sch-switch__index">{{ String(idx + 1).padStart(2, '0') }}</span>
          <span class="sch-switch__body">
            <strong class="sch-switch__title">{{ item.label }}</strong>
            <em class="sch-switch__focus">{{ item.meta.focus }}</em>
            <span class="sch-switch__meta">
              <span>10 天 · 周末授课</span>
              <span>{{ item.meta.tags?.[0] || item.shortLabel }}</span>
            </span>
          </span>
          <span class="sch-switch__price">
            <b>{{ formatCoursePrice(item.meta.price) }}</b>
            <small>/ {{ item.meta.priceUnit || '人' }}</small>
          </span>
        </button>
      </div>

      <section class="sch-hero">
        <div class="sch-hero__text">
          <p class="sch-kicker">培训安排 · {{ activeSchedule.shortLabel }}</p>
          <h1>{{ meta.title }}</h1>
          <p class="sch-intro">
            由{{ meta.provider }}提供服务。{{ meta.goal }}
            培训时间：{{ meta.scheduleText }}（周末授课）。
          </p>
          <p class="sch-price">
            <em>培训报价</em>
            <strong>{{ formatCoursePrice(meta.price) }}</strong>
            <span>/ {{ meta.priceUnit || '人' }}</span>
          </p>
        </div>
        <div class="sch-hero__actions">
          <button type="button" class="sch-cta" @click="enrollOpen = true">报名参加本培训</button>
          <a
            v-if="meta.scheduleFile"
            class="sch-cta sch-cta--ghost"
            :href="meta.scheduleFile"
            :download="meta.scheduleFileName"
          >
            下载安排表
          </a>
        </div>
      </section>

      <div class="sch-meta">
        <div v-for="item in metaItems" :key="item.label" class="sch-meta__item">
          <span class="sch-meta__label">{{ item.label }}</span>
          <strong>{{ item.value }}</strong>
        </div>
      </div>

      <section class="sch-panel">
        <header class="sch-panel__head">
          <h2>十天培训安排表</h2>
          <span>{{ meta.panelHint }}</span>
        </header>

        <ol class="sch-timeline">
          <li v-for="course in courses" :key="`${activeId}-${course.day}`" class="sch-day">
            <div class="sch-day__rail" aria-hidden="true">
              <span class="sch-day__dot">{{ course.day }}</span>
            </div>
            <article class="sch-day__card">
              <header class="sch-day__head">
                <span class="sch-badge">{{ course.dateLabel }} · {{ course.weekday }}</span>
                <span class="sch-badge sch-badge--soft">第 {{ course.day }} 天 · {{ course.stage }}</span>
                <span v-if="course.practiceRatio" class="sch-badge sch-badge--cat">实操 {{ course.practiceRatio }}</span>
                <h3>{{ course.title }}</h3>
              </header>
              <p class="sch-day__desc">{{ course.content }}</p>
              <ul v-if="course.topics?.length" class="sch-day__topics">
                <li v-for="(topic, idx) in course.topics" :key="idx">{{ topic }}</li>
              </ul>
              <p class="sch-day__place">培训地点：{{ meta.location }}</p>
            </article>
          </li>
        </ol>
      </section>

      <section v-if="stages.length" class="sch-panel sch-panel--stages">
        <header class="sch-panel__head">
          <h2>培训阶段说明</h2>
          <span>{{ meta.stagesHint }}</span>
        </header>
        <div class="sch-stages">
          <article v-for="(stage, idx) in stages" :key="idx" class="sch-stage">
            <h3>{{ stage.title }}</h3>
            <p>{{ stage.desc }}</p>
          </article>
        </div>
      </section>

      <div class="sch-aside">
        <section class="sch-aside__col">
          <h3>培训目标</h3>
          <ol>
            <li v-for="(item, idx) in purposes" :key="idx">{{ item }}</li>
          </ol>
        </section>
        <section class="sch-aside__col">
          <h3>培训注意事项与配套要求</h3>
          <ol>
            <li v-for="(item, idx) in notices" :key="idx">{{ item }}</li>
          </ol>
        </section>
      </div>
    </div>

    <AcademyEnrollModal v-model="enrollOpen" :course="enrollCourse" />
  </AcademyLayout>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import AcademyLayout from '../components/AcademyLayout.vue'
import AcademyEnrollModal from '../components/AcademyEnrollModal.vue'
import {
  trainingSchedules,
  getTrainingSchedule,
  formatCoursePrice,
} from '../data/trainingCatalog'

const route = useRoute()
const router = useRouter()
const enrollOpen = ref(false)

const activeId = ref(
  trainingSchedules.some((s) => s.id === route.query.schedule)
    ? route.query.schedule
    : trainingSchedules[0].id
)

watch(
  () => route.query.schedule,
  (id) => {
    if (id && trainingSchedules.some((s) => s.id === id)) {
      activeId.value = id
    }
  }
)

watch(activeId, (id) => {
  if (route.query.schedule !== id) {
    router.replace({ query: { ...route.query, schedule: id } })
  }
})

const activeSchedule = computed(() => getTrainingSchedule(activeId.value))
const meta = computed(() => activeSchedule.value.meta)
const courses = computed(() => activeSchedule.value.courses)
const purposes = computed(() => activeSchedule.value.purposes)
const notices = computed(() => activeSchedule.value.notices)
const stages = computed(() => activeSchedule.value.stages)

const enrollCourse = computed(() => ({
  id: activeSchedule.value.id,
  shortTitle: activeSchedule.value.shortLabel,
  title: meta.value.title,
  duration: meta.value.duration,
  price: meta.value.price,
}))

const metaItems = computed(() => [
  { label: '培训时间', value: meta.value.scheduleText },
  { label: '培训地点', value: meta.value.location },
  { label: '培训报价', value: `${formatCoursePrice(meta.value.price)} / ${meta.value.priceUnit || '人'}` },
  {
    label: '主讲老师',
    value: `${meta.value.teacher}${meta.value.teacherPhone ? ` ${meta.value.teacherPhone}` : ''}`.trim(),
  },
  { label: '核心设备', value: meta.value.equipment },
])
</script>

<style scoped>
.ac-wrap {
  width: min(1080px, calc(100% - 40px));
  margin: 0 auto;
}

.sch-page {
  padding: 28px 0 8px;
}

.sch-switch {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
  margin-bottom: 18px;
}

.sch-switch__btn {
  position: relative;
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 14px 16px;
  min-height: 108px;
  padding: 18px 20px;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  background: #fff;
  text-align: left;
  cursor: pointer;
  overflow: hidden;
  transition:
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    transform 0.22s ease,
    background 0.22s ease;
}

.sch-switch__btn::before {
  content: '';
  position: absolute;
  inset: 0 auto 0 0;
  width: 3px;
  background: transparent;
  transition: background 0.22s ease;
}

.sch-switch__index {
  display: grid;
  place-items: center;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 13px;
  font-weight: 800;
  letter-spacing: 0.04em;
  font-variant-numeric: tabular-nums;
  transition: background 0.22s ease, color 0.22s ease;
}

.sch-switch__body {
  display: flex;
  flex-direction: column;
  gap: 5px;
  min-width: 0;
}

.sch-switch__title {
  font-size: 16px;
  font-weight: 700;
  line-height: 1.3;
  color: #152033;
}

.sch-switch__focus {
  margin: 0;
  font-style: normal;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.sch-switch__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 10px;
  margin-top: 2px;
}

.sch-switch__meta > span {
  font-size: 11px;
  font-weight: 600;
  color: #64748b;
  padding: 2px 8px;
  border-radius: 999px;
  background: #f1f5f9;
}

.sch-switch__price {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  justify-content: center;
  gap: 2px;
  flex-shrink: 0;
  padding-left: 8px;
  border-left: 1px solid #eef2f7;
  min-width: 92px;
}

.sch-switch__price b {
  font-size: 20px;
  font-weight: 800;
  color: #ff6a00;
  letter-spacing: -0.01em;
  line-height: 1.1;
}

.sch-switch__price small {
  font-size: 12px;
  color: #94a3b8;
}

.sch-switch__btn:hover {
  border-color: rgba(45, 140, 240, 0.4);
  transform: translateY(-2px);
  box-shadow: 0 10px 24px rgba(30, 55, 90, 0.08);
}

.sch-switch__btn.is-active {
  border-color: #2d8cf0;
  background: linear-gradient(135deg, #f5faff 0%, #fff 55%, #fffaf5 100%);
  box-shadow: 0 10px 28px rgba(45, 140, 240, 0.14);
}

.sch-switch__btn.is-active::before {
  background: linear-gradient(180deg, #2d8cf0, #ff6a00);
}

.sch-switch__btn.is-active .sch-switch__index {
  background: rgba(45, 140, 240, 0.12);
  color: #2d8cf0;
}

.sch-switch__btn.is-active .sch-switch__title {
  color: #0f2744;
}

.sch-switch__btn.is-active .sch-switch__meta > span {
  background: rgba(45, 140, 240, 0.1);
  color: #2d8cf0;
}

.sch-hero {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 24px;
  padding: 28px 28px 24px;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e8eef5;
  box-shadow: 0 8px 24px rgba(30, 55, 90, 0.05);
}

.sch-kicker {
  margin: 0 0 8px;
  font-size: 12px;
  font-weight: 700;
  color: #2d8cf0;
  letter-spacing: 0.08em;
}

.sch-hero h1 {
  margin: 0 0 10px;
  font-size: clamp(22px, 2.4vw, 28px);
  line-height: 1.3;
  color: #152033;
}

.sch-intro {
  margin: 0;
  max-width: 680px;
  font-size: 14px;
  line-height: 1.75;
  color: #667085;
}

.sch-price {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin: 16px 0 0;
}

.sch-price em {
  font-style: normal;
  font-size: 13px;
  color: #98a2b3;
}

.sch-price strong {
  font-size: 28px;
  font-weight: 800;
  color: #ff6a00;
  letter-spacing: 0.02em;
}

.sch-price span {
  font-size: 13px;
  color: #98a2b3;
}

.sch-hero__actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
  flex-shrink: 0;
}

.sch-cta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 11px 22px;
  border: none;
  border-radius: 8px;
  background: #ff6a00;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  text-decoration: none;
  box-shadow: 0 8px 18px rgba(255, 106, 0, 0.25);
}

.sch-cta:hover {
  background: #e85f00;
}

.sch-cta--ghost {
  background: #fff;
  color: #2d8cf0;
  border: 1px solid #2d8cf0;
  box-shadow: none;
}

.sch-cta--ghost:hover {
  background: #f3f8ff;
}

.sch-meta {
  display: grid;
  grid-template-columns: repeat(5, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0 20px;
}

.sch-meta__item {
  padding: 16px 14px;
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  min-width: 0;
}

.sch-meta__label {
  display: block;
  margin-bottom: 8px;
  font-size: 12px;
  color: #98a2b3;
}

.sch-meta__item strong {
  display: block;
  font-size: 13px;
  font-weight: 600;
  line-height: 1.5;
  color: #1f2937;
  word-break: break-word;
}

.sch-panel {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 14px;
  padding: 24px 24px 12px;
  box-shadow: 0 8px 24px rgba(30, 55, 90, 0.04);
}

.sch-panel--stages {
  margin-top: 16px;
  padding-bottom: 20px;
}

.sch-panel__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 14px;
  border-bottom: 1px solid #eef2f7;
}

.sch-panel__head h2 {
  margin: 0;
  font-size: 20px;
  color: #152033;
}

.sch-panel__head span {
  font-size: 12px;
  color: #98a2b3;
  text-align: right;
}

.sch-timeline {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sch-day {
  display: grid;
  grid-template-columns: 44px 1fr;
  gap: 0 14px;
}

.sch-day__rail {
  position: relative;
  display: flex;
  justify-content: center;
  padding-top: 20px;
}

.sch-day__rail::before {
  content: '';
  position: absolute;
  top: 0;
  bottom: 0;
  left: 50%;
  width: 2px;
  transform: translateX(-50%);
  background: linear-gradient(180deg, #d7e6f8, #edf1f7);
}

.sch-day:first-child .sch-day__rail::before {
  top: 28px;
}

.sch-day:last-child .sch-day__rail::before {
  bottom: calc(100% - 40px);
}

.sch-day__dot {
  position: relative;
  z-index: 1;
  width: 30px;
  height: 30px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  background: #fff;
  border: 2px solid #2d8cf0;
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 700;
  box-shadow: 0 0 0 4px rgba(45, 140, 240, 0.1);
}

.sch-day__card {
  margin-bottom: 14px;
  padding: 18px 20px 16px;
  border: 1px solid #e8eef5;
  border-radius: 12px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.sch-day:nth-child(odd) .sch-day__card {
  background: #ffffff;
  border-color: #e8eef5;
}

.sch-day:nth-child(even) .sch-day__card {
  background: #dceefb;
  border-color: #c5dff5;
}

.sch-day__card:hover {
  border-color: rgba(45, 140, 240, 0.35);
  box-shadow: 0 10px 24px rgba(40, 80, 140, 0.06);
}

.sch-day__head {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}

.sch-day__head h3 {
  margin: 0;
  flex: 1 1 100%;
  font-size: 16px;
  font-weight: 700;
  color: #152033;
  line-height: 1.4;
}

.sch-badge {
  display: inline-flex;
  padding: 3px 10px;
  border-radius: 999px;
  background: rgba(45, 140, 240, 0.12);
  color: #2d8cf0;
  font-size: 12px;
  font-weight: 700;
}

.sch-badge--soft {
  background: #eef2f7;
  color: #667085;
}

.sch-badge--cat {
  background: rgba(255, 106, 0, 0.1);
  color: #ff6a00;
}

.sch-day__desc {
  margin: 0 0 8px;
  font-size: 13px;
  line-height: 1.75;
  color: #5b6573;
}

.sch-day__topics {
  margin: 0 0 10px;
  padding-left: 18px;
  color: #5b6573;
}

.sch-day__topics li {
  margin-bottom: 4px;
  font-size: 12px;
  line-height: 1.65;
}

.sch-day__place {
  margin: 0;
  font-size: 12px;
  color: #98a2b3;
}

.sch-stages {
  display: grid;
  gap: 12px;
}

.sch-stage {
  padding: 14px 16px;
  border-radius: 10px;
  background: #f7f9fc;
  border: 1px solid #eef2f7;
}

.sch-stage h3 {
  margin: 0 0 6px;
  font-size: 14px;
  color: #152033;
}

.sch-stage p {
  margin: 0;
  font-size: 13px;
  line-height: 1.7;
  color: #5b6573;
}

.sch-aside {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 16px;
}

.sch-aside__col {
  background: #fff;
  border: 1px solid #e8eef5;
  border-radius: 14px;
  padding: 20px 20px 12px;
}

.sch-aside__col h3 {
  margin: 0 0 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid #eef2f7;
  font-size: 16px;
  color: #152033;
}

.sch-aside__col ol {
  margin: 0;
  padding-left: 18px;
}

.sch-aside__col li {
  margin-bottom: 10px;
  font-size: 13px;
  line-height: 1.7;
  color: #5b6573;
}

@media (max-width: 960px) {
  .sch-meta {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .sch-aside {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 760px) {
  .ac-wrap {
    width: calc(100% - 24px);
  }

  .sch-switch {
    grid-template-columns: 1fr;
  }

  .sch-switch__btn {
    grid-template-columns: auto 1fr;
    gap: 12px;
    min-height: 0;
    padding: 16px;
  }

  .sch-switch__price {
    grid-column: 2;
    flex-direction: row;
    align-items: baseline;
    justify-content: flex-start;
    gap: 6px;
    padding-left: 0;
    border-left: none;
    min-width: 0;
  }

  .sch-switch__price b {
    font-size: 18px;
  }

  .sch-hero {
    flex-direction: column;
    align-items: stretch;
    padding: 20px;
  }

  .sch-hero__actions,
  .sch-cta {
    width: 100%;
  }

  .sch-meta {
    grid-template-columns: 1fr;
  }

  .sch-panel {
    padding: 18px 14px 8px;
  }

  .sch-panel__head {
    flex-direction: column;
  }

  .sch-day {
    grid-template-columns: 28px 1fr;
  }
}
</style>
